import { useAuthApi } from "@shared/api";
import { LocalStorageKey } from "@shared/entities";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { IUserDto } from "../entities/IUserDto";
import jsonwebtoken from "jsonwebtoken";
import { useNavigate } from "react-router-dom";
import { useLoading } from "./LoadingService";
import { RouteNames } from "@shared/entities/RouteNames";
import { DevLog } from "@shared/Utils";
import { AxiosError, isAxiosError } from "axios";

/** 處理登入相關的服務 */
interface IAuthService {
  /**
   * 進行登入
   * @param email {string} 帳號
   * @param password {string} 密碼
   */
  Login: (
    email: string,
    password: string,
    recaptcha: string,
    userLanguage?: string
  ) => Promise<any>;

  /** OAuth登入 */
  LoginByOAuth: (provider: string, code: string, userLanguage: string) => Promise<any>;

  /** 登出 */
  Logout: () => Promise<any>;

  /** 換發新的JWT Token */
  RefreshJWTToken: () => Promise<{ success: boolean; jwt: string }>;

  /** 目前登入中的使用者 */
  user?: IUserDto;

  /** 前往GoogleAuth */
  GoToOAuthGoogle: () => Promise<void>;

}

const Context = createContext<IAuthService>(null);

export function useAuthService() {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useAuthService 必須在AuthService之下層執行！");
  }
  return context;
}

export const AuthService = ({ children }) => {
  const { Login: apiLogin, Logout: apiLogout, DoJWTToken, GetOAuthProvider, LoginByOAuth: apiOAuthLogin } = useAuthApi();

  const navigate = useNavigate();
  const { setBlockUI } = useLoading();

  const [user, setUser] = useState<IUserDto>(
    JSON.parse(localStorage.getItem(LocalStorageKey.User))
  );

  /** 登入 */
  const Login = useCallback(
    async (
      email: string,
      password: string,
      recaptcha: string,
      userLanguage: string
    ) => {
      try {
        setBlockUI("Loading");

        // TODO: 嘿，我納悶為何userLanguage會超過八個字...
        const resp = await apiLogin(
          email, password,
          recaptcha,
          userLanguage.substring(0, 8)
        );

        // 正常登入時
        if (resp.success) {
          // 將JWT和RefreshToken存入LocalStorage
          localStorage.setItem(LocalStorageKey.RefreshToken, resp.refreshToken);
          localStorage.setItem(LocalStorageKey.AccessToken, resp.jwt);

          const user = jsonwebtoken.decode(resp.jwt) as IUserDto;
          localStorage.setItem(LocalStorageKey.User, JSON.stringify(user));
          setUser(user);
          DevLog("resp", resp, "user", user);
          navigate("/", { replace: true });
        }
      } catch (err) {
        console.error(err);
        if (isAxiosError(err)) {
          const resp = err.response.data;
          // 信箱未驗證
          if (resp.code === "user.errors.emailNotVerified") {
            navigate(`/${RouteNames.Register}/${RouteNames.Register_Mail}`, {
              replace: true,
              state: { email },
            });
          }
          // 信箱未驗證
          else if (resp.code === "user.errors.phoneNotVerified") {
            navigate(`/${RouteNames.Register}/${RouteNames.Register_SMS}`, {
              replace: true,
              state: { email },
            });
          }
        }
      } finally {
        setBlockUI();
      }
    },
    [apiLogin, navigate, setBlockUI]
  );

  /** OAuth登入 */
  const LoginByOAuth = useCallback(async (provider: string, code: string, userLanguage: string) => {

    try {
      setBlockUI("Loading");

      // TODO: 嘿，我納悶為何userLanguage會超過八個字...
      const resp = await apiOAuthLogin(
        provider, code,
        userLanguage.substring(0, 8)
      );

      // 正常登入時
      if (resp.success) {
        // 將JWT和RefreshToken存入LocalStorage
        localStorage.setItem(LocalStorageKey.RefreshToken, resp.refreshToken);
        localStorage.setItem(LocalStorageKey.AccessToken, resp.jwt);

        const user = jsonwebtoken.decode(resp.jwt) as IUserDto;
        localStorage.setItem(LocalStorageKey.User, JSON.stringify(user));
        setUser(user);
        DevLog("resp", resp, "user", user);
        navigate("/", { replace: true });
      }
      // 通常未驗證的人也不可能綁OAuth

    } catch (err) {
      console.error(err);
    } finally {
      setBlockUI();
    }
  }, [apiOAuthLogin, navigate, setBlockUI])
  /** 登出 */
  const Logout = useCallback(async () => {
    try {
      setBlockUI("Loading");

      const refreshToken = localStorage.getItem(LocalStorageKey.RefreshToken);
      await apiLogout(refreshToken);
    } finally {
      setUser(null);
      localStorage.clear();
      navigate("/");
      setBlockUI();
    }
  }, [apiLogout, navigate, setBlockUI]);

  /** 換發新的JWT Token */
  const RefreshJWTToken = useCallback(async () => {
    const refreshToken = localStorage.getItem(LocalStorageKey.RefreshToken);
    try {
      const resp = await DoJWTToken(refreshToken);
      if (resp) {
        // 成功取得新的JWT Token時?
        localStorage.setItem(LocalStorageKey.AccessToken, resp.jwt);

        const user = jsonwebtoken.decode(resp.jwt) as IUserDto;
        localStorage.setItem(LocalStorageKey.User, JSON.stringify(user));
        setUser(user);
        return resp;
      }
    }
    catch {
      await Logout()
    }
  }, [DoJWTToken, Logout]);

  const GoToOAuthGoogle = useCallback(async () => {
    setBlockUI('Login with Google')
    try {
      const resp = await GetOAuthProvider()
      if (resp.success) {
        const url = resp.data.url;
        window.location.href = url;
      }
    } finally {
      setBlockUI()
    }
  }, [GetOAuthProvider, setBlockUI,])
  const context: IAuthService = useMemo(
    () => ({
      Login,
      LoginByOAuth,
      Logout,
      user,
      RefreshJWTToken,
      GoToOAuthGoogle,
    }),
    [Login, LoginByOAuth, Logout, RefreshJWTToken, user, GoToOAuthGoogle]
  );
  return <Context.Provider value={context}>{children}</Context.Provider>;
};
